// Chat utilities for common conversation operations
export const useChatUtils = () => {
  const { user } = storeToRefs(useAuthStore());

  const ROLE_LABELS = {
    accounts: "Accounts",
    admin: "Admin",
    buyer: "Customer",
    seller: "Vendor",
    agent: "Agent",
    kam: "KAM",
  };

  /**
   * Get the display label for a role
   * @param {string} role - The role identifier (buyer/seller)
   * @returns {string} The display label (Customer/Vendor) or original role if not found
   */
  const getRoleLabel = (role) => {
    if (!role) return "";

    const roleLower = role.toLowerCase();

    return ROLE_LABELS[roleLower] || role;
  };

  /**
   * Normalize phone number
   * Cleans phone number by removing special characters and country code,
   * then optionally adds +91 prefix
   * @param {string} phone - The phone number to normalize
   * @param {Object} options - Configuration options
   * @param {boolean} options.includeIsdCode - Whether to add +91 prefix (default: true)
   * @returns {string} Normalized phone number
   */
  const normalizePhone = (phone, { includeIsdCode = true } = {}) => {
    if (!phone) return "";

    let normalized = String(phone).trim();

    // Remove +, spaces, and hyphens
    normalized = normalized.replace(/[\s\-+]/g, "");

    // If starts with 91 and has more than 10 digits, remove country code
    if (normalized.startsWith("91") && normalized.length > 10) {
      normalized = normalized.slice(2);
    }

    // Get last 10 digits
    normalized = normalized.slice(-10);

    return includeIsdCode ? `+91${normalized}` : normalized;
  };

  // Check if user is participant in conversation
  const isUserParticipant = async (conversation) => {
    try {
      const currentUserPhone = unref(user)?.phone;

      if (!currentUserPhone) return false;

      const normalizedUserPhone = normalizePhone(currentUserPhone);

      const participant =
        await conversation.getParticipantByIdentity(normalizedUserPhone);

      return Boolean(participant);
    } catch {
      return false;
    }
  };

  /**
   * Open inbox in new tab with optional conversationSid
   * @param {string|Object} sidOrConversation - ConversationSid string or conversation object
   * @returns {void}
   */
  const openConversationInbox = (sidOrConversation = null) => {
    if (sidOrConversation) {
      sidOrConversation = unref(sidOrConversation);
    }

    let conversationSid = sidOrConversation?.sid || sidOrConversation;

    if (conversationSid) {
      // Open inbox in new tab with conversationSid as query param
      const url = `/inbox?conversationSid=${encodeURIComponent(conversationSid)}`;

      window.open(url, "_blank");
    } else {
      // Fallback: open inbox without conversationSid param
      window.open("/inbox", "_blank");
    }
  };

  return {
    // Utility methods for conversations
    getRoleLabel,
    isUserParticipant,
    normalizePhone,

    // Navigation helpers
    openConversationInbox,
  };
};
